import React, { useState, useEffect } from 'react';
import './SettingsModal.css';

const ENGINE_INFO = {
  seedvr2: {
    name: 'SeedVR2',
    tag: 'Local · Recommended',
    description: 'One-step diffusion upscaler. Highest quality, upscales directly to your target size on this machine’s GPU.'
  },
  esrgan: {
    name: 'ESRGAN',
    tag: 'Local',
    description: 'Classic Real-ESRGAN 4× upscaler. Fast and light — good fallback on machines without SeedVR2.'
  },
  cloud: {
    name: 'Cloud',
    tag: 'Server · SeedVR2',
    description: 'SeedVR2 running on PixlSizer’s servers (Real-ESRGAN fallback). Works everywhere, uses your monthly AI credits.'
  }
};

const FILL_ENGINE_INFO = {
  'flux-local': {
    name: 'FLUX Fill Dev',
    tag: 'Local · Recommended',
    description: 'FLUX.1 Fill [dev] running on this machine’s GPU. Highest quality border fill, no upload, no payload limits.'
  },
  cloud: {
    name: 'FLUX Fill Pro',
    tag: 'Cloud',
    description: 'FLUX Fill Pro via Replicate on PixlSizer’s servers. Works everywhere, uses your monthly AI credits.'
  }
};

const SettingsModal = ({ isOpen, onClose }) => {
  const [aiConfig, setAiConfig] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // null when idle, { phase: 'download' | 'extract', percent } while installing
  const [esrganDownload, setEsrganDownload] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadConfig();
      setSaveError(null);
    }
  }, [isOpen]);

  const loadConfig = async () => {
    try {
      if (window.electronAPI && window.electronAPI.getAIConfig) {
        const config = await window.electronAPI.getAIConfig();
        setAiConfig(config);
      }
    } catch (error) {
      console.error('Failed to load AI config:', error);
    }
  };

  const isEngineAvailable = (engine) => {
    if (!aiConfig) return false;
    if (engine === 'seedvr2') return aiConfig.seedvr2Available;
    if (engine === 'esrgan') return aiConfig.localProcessingEnabled;
    return true; // cloud always available
  };

  const isFillEngineAvailable = (engine) => {
    if (!aiConfig) return false;
    if (engine === 'flux-local') return aiConfig.fluxFillAvailable;
    return true; // cloud always available
  };

  const handleSelectEngine = async (engine) => {
    if (!isEngineAvailable(engine)) return;
    setSaveError(null);
    try {
      const result = await window.electronAPI.setUpscaleEngine(engine);
      if (result.success) {
        await loadConfig();
      } else {
        setSaveError(result.error || 'Could not change engine');
      }
    } catch (error) {
      console.error('Failed to set upscale engine:', error);
      setSaveError('Could not change engine');
    }
  };

  const handleDownloadEsrgan = async () => {
    if (esrganDownload) return;
    setSaveError(null);
    setEsrganDownload({ phase: 'download', percent: 0 });
    const unsubscribe = window.electronAPI.onEsrganDownloadProgress
      ? window.electronAPI.onEsrganDownloadProgress((progress) => setEsrganDownload(progress))
      : null;
    try {
      const result = await window.electronAPI.downloadEsrgan();
      if (result.success) {
        await loadConfig();
        // Let the header pill (AIProcessingStatus) pick up the new engine
        window.dispatchEvent(new Event('ai-config-updated'));
      } else {
        setSaveError(result.error || 'ESRGAN download failed');
      }
    } catch (error) {
      console.error('ESRGAN download failed:', error);
      setSaveError('ESRGAN download failed — check your connection and try again.');
    } finally {
      if (unsubscribe) unsubscribe();
      setEsrganDownload(null);
    }
  };

  const handleSelectFillEngine = async (engine) => {
    if (!isFillEngineAvailable(engine)) return;
    setSaveError(null);
    try {
      const result = await window.electronAPI.setFillEngine(engine);
      if (result.success) {
        await loadConfig();
      } else {
        setSaveError(result.error || 'Could not change fill engine');
      }
    } catch (error) {
      console.error('Failed to set fill engine:', error);
      setSaveError('Could not change fill engine');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="settings-body">
          <div className="settings-section">
            <label className="settings-section-title">Default Upscale Engine</label>
            <p className="settings-section-hint">
              Used whenever Ai Upscale enlarges an image to your target size.
            </p>

            <div className="engine-options">
              {['seedvr2', 'esrgan', 'cloud'].map((engine) => {
                const info = ENGINE_INFO[engine];
                const available = isEngineAvailable(engine);
                const selected = aiConfig && aiConfig.upscaleEngine === engine;
                // ESRGAN can be installed on demand when it isn't bundled
                const downloadable = engine === 'esrgan' && !available;
                const downloading = downloadable && esrganDownload;
                const tag = available ? info.tag
                  : downloading ? (esrganDownload.phase === 'extract'
                      ? 'Installing…'
                      : `Downloading… ${esrganDownload.percent}%`)
                  : downloadable ? 'Not installed · Click to download (~50 MB)'
                  : 'Not available on this machine';
                return (
                  <button
                    key={engine}
                    className={`engine-option ${selected ? 'selected' : ''} ${available ? '' : downloadable ? 'downloadable' : 'unavailable'}`}
                    onClick={() => (downloadable ? handleDownloadEsrgan() : handleSelectEngine(engine))}
                    disabled={(!available && !downloadable) || !!downloading}
                  >
                    <span className="engine-radio">
                      <span className="engine-radio-dot"></span>
                    </span>
                    <span className="engine-details">
                      <span className="engine-name-row">
                        <span className="engine-name">{info.name}</span>
                        <span className="engine-tag">{tag}</span>
                      </span>
                      <span className="engine-description">{info.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>

          </div>

          <div className="settings-section settings-section-spaced">
            <label className="settings-section-title">Default AI Fill Engine</label>
            <p className="settings-section-hint">
              Used by Aspect Crop's AI Fill to generate the expanded borders.
            </p>

            <div className="engine-options">
              {['flux-local', 'cloud'].map((engine) => {
                const info = FILL_ENGINE_INFO[engine];
                const available = isFillEngineAvailable(engine);
                const selected = aiConfig && aiConfig.fillEngine === engine;
                return (
                  <button
                    key={engine}
                    className={`engine-option ${selected ? 'selected' : ''} ${available ? '' : 'unavailable'}`}
                    onClick={() => handleSelectFillEngine(engine)}
                    disabled={!available}
                  >
                    <span className="engine-radio">
                      <span className="engine-radio-dot"></span>
                    </span>
                    <span className="engine-details">
                      <span className="engine-name-row">
                        <span className="engine-name">{info.name}</span>
                        <span className="engine-tag">{available ? info.tag : 'Not available on this machine'}</span>
                      </span>
                      <span className="engine-description">{info.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {saveError && <p className="settings-error">{saveError}</p>}
          </div>
        </div>

        <div className="settings-footer">
          <button className="btn-save" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
